"use client";

import { useQuery } from "@tanstack/react-query";
import { isValidPhoneNumber } from "react-phone-number-input";
import { quotePaymentPage } from "@/lib/api/payment-page";
import { quoteCheckout } from "@/lib/api/checkout";
import type { MarketQuote, MarketQuoteParams } from "@/lib/api/checkout.schema";

type QuoteFetcher = (
  params: MarketQuoteParams,
) => Promise<{ data?: MarketQuote; error?: string }>;

/** A quote is worth asking for once the amount is positive and the payer is identifiable. */
function isQuotable(params: Partial<MarketQuoteParams>): params is MarketQuoteParams {
  if (!params.amount || params.amount <= 0) return false;
  if (params.phone) return isValidPhoneNumber(params.phone);
  return !!params.country;
}

function useMarketQuote(
  scope: readonly unknown[],
  fetcher: QuoteFetcher,
  params: Partial<MarketQuoteParams>,
  enabled: boolean,
) {
  const quotable = enabled && isQuotable(params);
  return useQuery({
    queryKey: [...scope, "quote", params.phone ?? null, params.country ?? null, params.amount ?? 0],
    queryFn: async () => {
      const result = await fetcher(params as MarketQuoteParams);
      if (result.error) throw new Error(result.error);
      return result.data!;
    },
    enabled: quotable,
    retry: false,
    // Rates move; keep a quote only briefly, the checkout response is binding anyway
    staleTime: 30 * 1000,
  });
}

/** Quote on a payment page: `phone` for the real figure, `country` for a preview. */
export function usePageQuote(
  slug: string,
  params: Partial<MarketQuoteParams>,
  enabled = true,
) {
  return useMarketQuote(
    ["payment-page", slug],
    (p) => quotePaymentPage(slug, p),
    params,
    enabled && !!slug,
  );
}

/** Quote on a hosted checkout session. */
export function useCheckoutQuote(
  token: string,
  params: Partial<MarketQuoteParams>,
  enabled = true,
) {
  return useMarketQuote(
    ["checkout", token],
    (p) => quoteCheckout(token, p),
    params,
    enabled && !!token,
  );
}
